import React, { KeyboardEvent, useEffect, useRef, useState } from "react";
import { css, styled } from "styled-components";
import Label from "./Label";
import { Calendar, Delete, Icon } from "react-feather";
import Button from "./Button";
import Select from "./Select";

type Props = {
  required?: boolean;
  label?: React.ReactNode;
  autoFocus?: boolean;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  textarea?: boolean;
  className?: string;
  onSubmit?: () => void;
  icon?: Icon;
  clearable?: boolean;
  grow?: boolean;
  noPadding?: boolean;
  height?: number;
  width?: number | string;
  minWidth?: number | string;
  maxWidth?: number | string;
  submitIcon?: Icon;
  buttonSize?: number;
  onBlur?: () => void;
  onKeyDown?: (
    e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  transform?: (value: string) => string;
  invalid?: boolean;
  loading?: boolean;
  portalTarget?: HTMLElement | null;
} & (
  | {
      numeric?: false;
      value: string;
      onChange: (value: string) => void;
    }
  | { numeric: true; value: number; onChange: (value: number) => void }
);

export const StyledInput = styled.input<{
  $error?: boolean;
  $icon?: boolean;
  $noPadding?: boolean;
  $submitIcon?: boolean;
}>`
  background-color: var(--inputBackground);
  height: 39px;
  border: none !important;
  color: var(--gray400);
  width: 100% !important;
  box-sizing: border-box;
  font-size: 16px;
  padding: 0 10px;
  outline: none;
  border: 1px solid var(--inputBorder) !important;
  transition: all 100ms;
  font-family: inherit !important;
  margin-right: 0 !important;
  border-radius: 7px;
  color-scheme: dark;
  min-width: 0;

  ${(props) =>
    props.$submitIcon &&
    css`
      border-radius: 25px;
      height: 45px;
      padding: 0 20px;
    `}

  &:focus {
    background-color: var(--dark400);
    border: 1px solid var(--blue100) !important;

    ${(props) =>
      props.$error &&
      css`
        border-color: var(--red400) !important;
      `}
  }

  &:disabled {
    opacity: 0.5;
  }

  &::placeholder {
    color: var(--dark700);
  }

  &::-webkit-calendar-picker-indicator {
    display: none;
  }

  ${(props) =>
    props.$error &&
    css`
      border-color: var(--red400) !important;
    `}

  ${(props) =>
    props.$icon &&
    css`
      padding-left: 35px;
    `}
`;

const StyledTextarea = styled(StyledInput)`
  min-height: 80px;
  padding: 9px 10px;
  resize: vertical;
`;

function toCssSize(v: number | string): string {
  return typeof v === "number" ? `${v}px` : v;
}

const Wrapper = styled.div<{
  $pad: boolean;
  $grow?: boolean;
  $width?: number | string;
  $minWidth?: number | string;
  $maxWidth?: number | string;
}>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 5px;
  min-width: ${(props) =>
    props.$minWidth !== undefined ? toCssSize(props.$minWidth) : "150px"};
  flex: 1 1 0;

  > label {
    flex-basis: 100%;
  }

  ${(props) =>
    props.$width !== undefined &&
    css`
      width: ${toCssSize(props.$width)};
      flex: none;
    `}
  ${(props) =>
    props.$maxWidth !== undefined &&
    css`
      max-width: ${toCssSize(props.$maxWidth)};
    `}
  ${(props) =>
    props.$pad &&
    css`
      margin-bottom: 20px;
    `}
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 1 1 0;
  min-width: 0;

  > i {
    position: absolute;
    top: 0;
    height: 100%;
    display: flex;
    align-items: center;
    left: 10px;
    pointer-events: none;
    color: var(--gray200);
  }
`;

const Buttons = styled.div`
  padding: 0;
  position: absolute;
  right: 6px;
  height: 100%;
  top: 0;
  display: flex;
  align-items: center;
  gap: 5px;
`;

const StyledButton = styled(Button)`
  background-color: var(--blue060);
  --font-color: var(--gray500);
  margin-right: 2px;

  &:hover {
    background-color: var(--blue100);
    --font-color: var(--gray500);
  }
`;

const InputLoading = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid var(--blue300);
  border-top-color: transparent;
  animation: input_spinner 800ms linear infinite;
  margin: 0 5px;
  flex-shrink: 0;

  @keyframes input_spinner {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

// ---------------------------------------------------------------------------
// Timezone helpers
// ---------------------------------------------------------------------------

type TzMode = "server" | "local" | "utc";

const SERVER_TZ: string =
  (window as any).__SERVER_TIMEZONE__ ??
  Intl.DateTimeFormat().resolvedOptions().timeZone;
const LOCAL_TZ: string = Intl.DateTimeFormat().resolvedOptions().timeZone;

/** ISO string → datetime-local string ("yyyy-MM-ddTHH:mm") in the given tz */
function isoToLocal(iso: string, tz: TzMode): string {
  if (!iso) {
    return "";
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return "";
  }

  const timeZone =
    tz === "utc" ? "UTC" : tz === "server" ? SERVER_TZ : undefined;

  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(date);

  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? "00";
  const hour = get("hour") === "24" ? "00" : get("hour");
  return `${get("year")}-${get("month")}-${get("day")}T${hour}:${get(
    "minute"
  )}`;
}

/** datetime-local string ("yyyy-MM-ddTHH:mm") + tz → ISO string */
function localToIso(localStr: string, tz: TzMode): string {
  if (!localStr) {
    return "";
  }
  const [datePart, timePart = "00:00"] = localStr.split("T");
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute] = timePart.split(":").map(Number);

  if (tz === "utc") {
    return new Date(Date.UTC(year, month - 1, day, hour, minute)).toISOString();
  }
  if (tz === "local") {
    return new Date(year, month - 1, day, hour, minute).toISOString();
  }
  // Server tz: find the UTC instant that displays as this wall-clock time in SERVER_TZ.
  // Round-trip correction handles DST correctly.
  const candidate = new Date(Date.UTC(year, month - 1, day, hour, minute));
  const displayed = isoToLocal(candidate.toISOString(), "server");
  const diffMs = new Date(localStr).getTime() - new Date(displayed).getTime();
  return new Date(candidate.getTime() + diffMs).toISOString();
}

// ---------------------------------------------------------------------------
// Timezone selector
// ---------------------------------------------------------------------------

function getTzAbbr(tz?: string, date: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZoneName: "short",
      timeZone: tz,
    }).formatToParts(date);
    return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
  } catch {
    return tz ?? "";
  }
}

const TZ_OPTIONS: { value: TzMode; label: string }[] = [
  { value: "local", label: LOCAL_TZ },
  ...(LOCAL_TZ !== SERVER_TZ
    ? [{ value: "server" as TzMode, label: SERVER_TZ }]
    : []),
  { value: "utc", label: "UTC" },
];

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

const Input = (props: Props) => {
  const {
    required,
    autoFocus,
    value,
    onChange,
    placeholder,
    type,
    disabled,
    textarea,
    numeric,
    label,
    onSubmit,
    className,
    icon,
    clearable,
    grow,
    noPadding,
    height,
    width,
    minWidth,
    maxWidth,
    submitIcon,
    buttonSize,
    onBlur,
    onKeyDown,
    transform,
    invalid,
    loading,
    portalTarget,
  } = props;

  const isDatetimeLocal = type === "datetime-local";
  const isDate = type === "date";

  const [hasError, setHasError] = useState(false);
  const [tzMode, setTzMode] = useState<TzMode>("local");

  // For datetime-local: inputValue is always the "yyyy-MM-ddTHH:mm" display string.
  // For everything else: it mirrors the raw value.
  function toDateString(value: string): string {
    if (!value) {
      return "";
    }
    // Already yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return value;
    }
    // ISO or any other parseable date
    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return "";
    }
    return date.toISOString().slice(0, 10);
  }

  const toDisplayValue = (v: string | number | undefined): string => {
    if (isDatetimeLocal) {
      return isoToLocal((v as string) ?? "", tzMode);
    }
    if (isDate) {
      return toDateString((v as string) ?? "");
    }
    return v !== undefined ? String(v) : "";
  };

  const [inputValue, setInputValue] = useState<string>(() =>
    toDisplayValue(value)
  );
  const [previousValue, setPreviousValue] = useState<
    string | number | undefined
  >(value);

  const inputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Sync when external value changes
  useEffect(() => {
    if (value !== previousValue) {
      setPreviousValue(value);
      setInputValue(toDisplayValue(value));
    }
  }, [value, previousValue]);

  // Tz mode change: keep wall-clock time fixed, re-emit ISO with new tz
  const handleTzChange = (newTz: TzMode) => {
    setTzMode(newTz);
    // inputValue (wall-clock) stays exactly the same — just re-interpret it in the new tz
    if (inputValue && !numeric) {
      (onChange as (v: string) => void)(localToIso(inputValue, newTz));
    }
  };

  const windowWidth =
    window.innerWidth > 0 ? window.innerWidth : window.screen.width;

  const showClearButton = clearable && value;
  const showSubmitButton = submitIcon;
  const showCalendarButton = isDate || isDatetimeLocal;
  const paddingRight = `${
    (showClearButton ? 30 : 0) +
    (showSubmitButton ? 35 : 0) +
    (showCalendarButton ? 30 : 0) +
    (loading ? 30 : 0) +
    5
  }px`;

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const raw = e.target.value;

    if (numeric) {
      const valueAsNumber = raw as unknown as number;
      if (valueAsNumber && !isNaN(valueAsNumber) && valueAsNumber % 1 === 0) {
        setHasError(false);
        (onChange as (v: number) => void)(parseInt(raw));
      } else {
        setHasError(true);
      }
      setInputValue(raw);
      return;
    }

    const transformed = transform ? transform(raw) : raw;
    setInputValue(transformed);

    if (isDatetimeLocal) {
      (onChange as (v: string) => void)(raw ? localToIso(raw, tzMode) : "");
    } else {
      // date (yyyy-mm-dd) and all other types pass through as-is
      (onChange as (v: string) => void)(transformed);
    }
  };

  const handleClear = () => {
    if (numeric) {
      return;
    }
    (onChange as (v: string) => void)("");
    setInputValue("");
    textareaRef.current?.focus();
    inputRef.current?.focus();
  };

  const inputProps = {
    autoFocus: windowWidth >= 700 && autoFocus,
    value: inputValue,
    onChange: handleChange,
    spellCheck: false,
    placeholder,
    type,
    disabled,
    $error: hasError || invalid,
    $icon: !!icon,
    $submitIcon: !!showSubmitButton,
    onKeyUp: (event: KeyboardEvent) => {
      if (event.key === "Enter") {
        onSubmit?.();
      }
    },
    size: 1,
    onBlur,
    onKeyDown,
  };

  return (
    <Wrapper
      $pad={!!label && !noPadding}
      $grow={grow}
      $width={width}
      $minWidth={minWidth}
      $maxWidth={maxWidth}
      className={className}
    >
      {label && (
        <Label
          label={label}
          required={required}
          hasValue={numeric ? value !== undefined : !!value}
        />
      )}
      <InputWrapper>
        {icon && <i>{React.createElement(icon, { size: 16 })}</i>}
        {textarea ? (
          <StyledTextarea
            as="textarea"
            {...inputProps}
            ref={textareaRef}
            style={{ minHeight: height, paddingRight }}
          />
        ) : (
          <StyledInput
            {...inputProps}
            ref={inputRef}
            style={{ height, paddingRight }}
          />
        )}
        <Buttons>
          {showClearButton && (
            <Button
              icon={Delete}
              onClick={handleClear}
              iconSize={19}
              type="danger"
              borderless
              size={buttonSize ?? 30}
              tooltip="Reset"
              tabIndex={-1}
            />
          )}
          {showCalendarButton && (
            <Button
              icon={Calendar}
              onClick={() => inputRef.current?.showPicker()}
              size={buttonSize ?? 30}
              iconSize={15}
              tabIndex={-1}
            />
          )}
          {loading && <InputLoading />}
          {showSubmitButton && (
            <StyledButton
              icon={submitIcon}
              onClick={onSubmit}
              type="default"
              size={buttonSize ?? 30}
              borderless
            />
          )}
        </Buttons>
      </InputWrapper>
      {isDatetimeLocal && (
        <Select
          value={tzMode}
          onChange={({ value }) => handleTzChange(value as TzMode)}
          options={TZ_OPTIONS.map((o) => ({
            ...o,
            label: getTzAbbr(o.label, value ? new Date(value) : new Date()),
          }))}
          buttonSize={buttonSize ?? 30}
          width={80}
          portalTarget={portalTarget}
        />
      )}
    </Wrapper>
  );
};

export default Input;
